import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';
import '../../../styles/admin-blog-editor.css';

interface BlogCategory { uid: string; name: string; slug: string; }
interface BlogTag { uid: string; name: string; slug: string; }
interface BlogPost {
  uid: string;
  slug: string;
  title: string;
  excerpt?: string;
  content?: string;
  cover_image_url?: string;
  image_prompt?: string;
  status: string;
  published_at?: string;
  author_name?: string;
  meta_title?: string;
  meta_description?: string;
  category?: BlogCategory;
  tags: BlogTag[];
  created_at: string;
  updated_at: string;
}

interface FormState {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_name: string;
  status: 'draft' | 'scheduled' | 'published';
  published_at: string;
  meta_title: string;
  meta_description: string;
  category_uid: string;
  tag_uids: string[];
  cover_image_url: string;
  image_prompt: string;
}

const EMPTY_FORM: FormState = {
  title: '', slug: '', excerpt: '', content: '',
  author_name: '', status: 'draft', published_at: '',
  meta_title: '', meta_description: '',
  category_uid: '', tag_uids: [], cover_image_url: '', image_prompt: '',
};

const ADMIN_NAV_LINK = ({ isActive }: { isActive: boolean }) =>
  `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`;

function slugify(v: string): string {
  return v.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-');
}

function toDatetimeLocal(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

const AdminBlogEditor: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const isNew = uid === 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'editor' | 'preview' | 'split'>('split');
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [postUid, setPostUid] = useState<string | null>(isNew ? null : uid ?? null);
  const [tagFilter, setTagFilter] = useState('');

  useEffect(() => {
    api.get<BlogCategory[]>(API_ENDPOINTS.blog.categories).then(setCategories).catch(() => {});
    api.get<BlogTag[]>(API_ENDPOINTS.blog.tags).then(setTags).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get<BlogPost>(API_ENDPOINTS.admin.blogPost(uid!))
      .then(post => {
        setForm({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt || '',
          content: post.content || '',
          author_name: post.author_name || '',
          status: post.status as 'draft' | 'scheduled' | 'published',
          published_at: toDatetimeLocal(post.published_at),
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          category_uid: post.category?.uid || '',
          tag_uids: post.tags.map(t => t.uid),
          cover_image_url: post.cover_image_url || '',
          image_prompt: post.image_prompt || '',
        });
        setPostUid(post.uid);
      })
      .catch(() => setError('Failed to load post'))
      .finally(() => setLoading(false));
  }, [uid, isNew]);

  useEffect(() => {
    document.title = isNew ? 'Admin | New Post' : 'Admin | Edit Post';
  }, [isNew]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'title' && isNew) {
        next.slug = slugify(value as string);
      }
      return next;
    });
  }, [isNew]);

  const toggleTag = (tagUid: string) => {
    setForm(prev => ({
      ...prev,
      tag_uids: prev.tag_uids.includes(tagUid)
        ? prev.tag_uids.filter(id => id !== tagUid)
        : [...prev.tag_uids, tagUid],
    }));
  };

  const buildPayload = (
    status: 'draft' | 'scheduled' | 'published',
    publishedAtOverride?: string | null,
  ) => {
    let publishedAt: string | null;
    if (publishedAtOverride !== undefined) {
      publishedAt = publishedAtOverride;
    } else if (status === 'draft') {
      publishedAt = null;
    } else {
      publishedAt = form.published_at ? new Date(form.published_at).toISOString() : null;
    }

    return {
      title: form.title,
      slug: form.slug,
      excerpt: form.excerpt || null,
      content: form.content || null,
      author_name: form.author_name || null,
      status,
      published_at: publishedAt,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      category_uid: form.category_uid || null,
      tag_uids: form.tag_uids,
      cover_image_url: form.cover_image_url || null,
      image_prompt: form.image_prompt || null,
    };
  };

  const handleSave = async (
    status: 'draft' | 'scheduled' | 'published',
    publishedAtOverride?: string | null,
  ) => {
    if (!form.title || !form.slug) {
      setError('Title and slug are required');
      return;
    }

    if (status === 'scheduled') {
      if (!form.published_at) {
        setError('Please choose a publish date in the future before scheduling.');
        return;
      }
      const target = new Date(form.published_at).getTime();
      if (isNaN(target) || target <= Date.now()) {
        setError('Scheduled publish date must be in the future.');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload(status, publishedAtOverride);
      if (isNew || !postUid) {
        const created = await api.post<BlogPost>(API_ENDPOINTS.admin.blog, payload);
        setPostUid(created.uid);
        setForm(prev => ({
          ...prev,
          status,
          published_at: payload.published_at ? toDatetimeLocal(payload.published_at) : '',
        }));
        navigate(`/admin/blog/${created.uid}`, { replace: true });
      } else {
        await api.put<BlogPost>(API_ENDPOINTS.admin.blogPost(postUid), payload);
        setForm(prev => ({
          ...prev,
          status,
          published_at: payload.published_at ? toDatetimeLocal(payload.published_at) : '',
        }));
      }
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = () => handleSave('published', new Date().toISOString());

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !postUid) return;
    setImageUploading(true);
    setUploadStatus(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('access_token') || '';
      const res = await fetch(API_ENDPOINTS.admin.blogPostImage(postUid), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({} as Record<string, unknown>));
      if (!res.ok || !data.cover_image_url) {
        throw new Error(
          (data.message as string) || (data.detail as string) || `Upload failed (HTTP ${res.status})`
        );
      }
      // The backend optimized the file to WebP and returned a cache-busted URL;
      // updating the form src reloads the preview immediately.
      setForm(prev => ({ ...prev, cover_image_url: data.cover_image_url as string }));
      setUploadStatus({ ok: true, text: '✓ Uploaded & optimized to WebP — preview updated' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Image upload failed';
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      // "Failed to fetch" is a network-level reject (no HTTP response) — usually
      // the file exceeds the server's body limit, a CORS issue, or connectivity.
      const hint = /failed to fetch|networkerror|load failed/i.test(msg)
        ? ` — network/size issue (file is ${sizeMb} MB; max 5 MB). Try a smaller image or check your connection.`
        : '';
      console.error('[cover upload] failed', { error: err, file: file.name, type: file.type, sizeMb });
      setUploadStatus({ ok: false, text: `Upload failed: ${msg}${hint}` });
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Blog', href: '/admin/blog' }, { label: 'Loading…' }]}>
        <div className="admin-loading">Loading…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbItems={[
        { label: 'Admin' },
        { label: 'Blog', href: '/admin/blog' },
        { label: isNew ? 'New post' : form.title || 'Edit post' },
      ]}
    >
      <nav className="admin-nav">
        <NavLink to="/admin" end className={ADMIN_NAV_LINK}>Overview</NavLink>
        <NavLink to="/admin/users" className={ADMIN_NAV_LINK}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={ADMIN_NAV_LINK}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={ADMIN_NAV_LINK}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={ADMIN_NAV_LINK}>Plans</NavLink>
        <NavLink to="/admin/blog" className={ADMIN_NAV_LINK}>Blog</NavLink>
      </nav>

      <div className="abe-layout">

        <div className="abe-main">

          <div className="abe-field">
            <label className="abe-label">Title <span className="abe-required">*</span></label>
            <input
              className="abe-input abe-input--title"
              placeholder="Post title…"
              value={form.title}
              onChange={e => setField('title', e.target.value)}
            />
          </div>

          <div className="abe-field">
            <label className="abe-label">Slug <span className="abe-required">*</span></label>
            <input
              className="abe-input"
              placeholder="post-slug"
              value={form.slug}
              onChange={e => setField('slug', e.target.value)}
            />
          </div>

          <div className="abe-field">
            <label className="abe-label">Excerpt</label>
            <textarea
              className="abe-input abe-textarea abe-textarea--sm"
              placeholder="Short description (shown in card and SEO meta)…"
              value={form.excerpt}
              onChange={e => setField('excerpt', e.target.value)}
            />
          </div>

          <div className="abe-field">
            <div className="abe-editor-header">
              <label className="abe-label" style={{ marginBottom: 0 }}>Content</label>
              <div className="abe-view-tabs">
                {(['editor', 'split', 'preview'] as const).map(m => (
                  <button
                    key={m}
                    className={`abe-view-tab${previewMode === m ? ' abe-view-tab--active' : ''}`}
                    onClick={() => setPreviewMode(m)}
                  >
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className={`abe-editor-wrap abe-editor-wrap--${previewMode}`}>
              {previewMode !== 'preview' && (
                <textarea
                  className="abe-editor-pane abe-editor-pane--editor"
                  placeholder="Write in Markdown…"
                  value={form.content}
                  onChange={e => setField('content', e.target.value)}
                  spellCheck
                />
              )}
              {previewMode !== 'editor' && (
                <div className="abe-editor-pane abe-editor-pane--preview">
                  {form.content ? (
                    <div className="abe-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {form.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="abe-preview-empty">Preview will appear here…</p>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        <aside className="abe-sidebar">

          <div className="abe-sidebar-card">
            <div className="abe-sidebar-title">Publish</div>

            <div className="abe-field">
              <label className="abe-label">Status</label>
              <span
                className={`admin-badge ${
                  form.status === 'published'
                    ? 'admin-badge--green'
                    : form.status === 'scheduled'
                      ? 'admin-badge--amber'
                      : 'admin-badge--gray'
                }`}
              >
                {form.status === 'scheduled' && form.published_at
                  ? `Scheduled · ${new Date(form.published_at).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}`
                  : form.status}
              </span>
            </div>

            {error && (
              <div className="abe-error">{error}</div>
            )}

            <div className="admin-btn-group" style={{ marginTop: 12, flexWrap: 'wrap', gap: 6 }}>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={() => handleSave('draft')}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={() => handleSave('scheduled')}
                disabled={saving}
                title="Publish automatically at the date below"
              >
                {saving ? 'Saving…' : 'Schedule'}
              </button>
              <button
                className="admin-btn admin-btn--primary"
                onClick={handlePublishNow}
                disabled={saving}
              >
                {saving ? 'Saving…' : form.status === 'published' ? 'Update & Publish Now' : 'Publish Now'}
              </button>
            </div>

            <div style={{ marginTop: 12, borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
              <button
                className="admin-btn admin-btn--outline"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => navigate('/admin/blog')}
              >
                ← Back to list
              </button>
            </div>
          </div>

          <div className="abe-sidebar-card">
            <div className="abe-sidebar-title">Cover image</div>
            {form.cover_image_url && (
              <img
                src={form.cover_image_url}
                alt="Cover"
                className="abe-cover-preview"
              />
            )}
            <div className="abe-field" style={{ marginTop: 8 }}>
              <label className="abe-label">Image URL</label>
              <input
                className="abe-input"
                placeholder="https://…"
                value={form.cover_image_url}
                onChange={e => setField('cover_image_url', e.target.value)}
              />
            </div>
            {postUid && (
              <div className="abe-field">
                <label className="abe-label">Upload to S3</label>
                <label className={`admin-btn admin-btn--outline${imageUploading ? ' abe-uploading' : ''}`} style={{ cursor: 'pointer' }}>
                  {imageUploading ? 'Uploading…' : 'Choose file'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                  />
                </label>
                {!postUid && (
                  <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, display: 'block' }}>
                    Save the post first to enable upload
                  </span>
                )}
              </div>
            )}
            {uploadStatus && (
              <div
                className={uploadStatus.ok ? 'abe-upload-ok' : 'abe-error'}
                style={{ marginTop: 8 }}
                role="status"
              >
                {uploadStatus.text}
              </div>
            )}
            <div className="abe-field" style={{ marginTop: 4 }}>
              <label className="abe-label">Image prompt</label>
              <textarea
                className="abe-input abe-textarea abe-textarea--sm"
                placeholder="Prompt used to generate the cover image"
                value={form.image_prompt}
                onChange={e => setField('image_prompt', e.target.value)}
              />
            </div>
          </div>

          <div className="abe-sidebar-card">
            <div className="abe-sidebar-title">Author & date</div>
            <div className="abe-field">
              <label className="abe-label">Author name</label>
              <input
                className="abe-input"
                placeholder="e.g. OfferFlow Team"
                value={form.author_name}
                onChange={e => setField('author_name', e.target.value)}
              />
            </div>
            <div className="abe-field">
              <label className="abe-label">Published at</label>
              <input
                type="datetime-local"
                className="abe-input"
                value={form.published_at}
                onChange={e => setField('published_at', e.target.value)}
              />
            </div>
          </div>

          <div className="abe-sidebar-card">
            <div className="abe-sidebar-title">Category</div>
            <select
              className="abe-input"
              value={form.category_uid}
              onChange={e => setField('category_uid', e.target.value)}
            >
              <option value="">— None —</option>
              {categories.map(c => (
                <option key={c.uid} value={c.uid}>{c.name}</option>
              ))}
            </select>
          </div>

          {tags.length > 0 && (
            <div className="abe-sidebar-card">
              <div className="abe-sidebar-title">
                Tags <span className="abe-tag-count">{form.tag_uids.length} sel · {tags.length} total</span>
              </div>
              <input
                className="abe-input abe-tag-search"
                placeholder="Filter tags…"
                value={tagFilter}
                onChange={e => setTagFilter(e.target.value)}
              />
              <div className="abe-tag-list">
                {tags
                  .filter(t =>
                    !tagFilter ||
                    t.name.toLowerCase().includes(tagFilter.toLowerCase()) ||
                    t.slug.includes(tagFilter.toLowerCase())
                  )
                  .map(tag => (
                    <label key={tag.uid} className="abe-tag-item">
                      <input
                        type="checkbox"
                        checked={form.tag_uids.includes(tag.uid)}
                        onChange={() => toggleTag(tag.uid)}
                        className="abe-tag-checkbox"
                      />
                      <span>{tag.name}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          <div className="abe-sidebar-card">
            <div className="abe-sidebar-title">SEO</div>
            <div className="abe-field">
              <label className="abe-label">Meta title</label>
              <input
                className="abe-input"
                placeholder="Page title for search engines"
                value={form.meta_title}
                onChange={e => setField('meta_title', e.target.value)}
              />
              <span className="abe-hint">{form.meta_title.length}/60</span>
            </div>
            <div className="abe-field">
              <label className="abe-label">Meta description</label>
              <textarea
                className="abe-input abe-textarea abe-textarea--sm"
                placeholder="Short description for search results"
                value={form.meta_description}
                onChange={e => setField('meta_description', e.target.value)}
              />
              <span className="abe-hint">{form.meta_description.length}/160</span>
            </div>
          </div>

        </aside>
      </div>
    </PageLayout>
  );
};

export default AdminBlogEditor;
